import { http, HttpResponse, delay } from 'msw';

import { simpleUser } from '@/mocks/data/userData';
import type {
  AiConversationDto,
  UpdateMessageFeedbackResponseDto,
} from '@/redux/slices/gigawattBackendApiSlice';
import type { FeedbackReason } from '@/types/aiChat';

// CopilotKit (AG-UI protocol, REST transport) endpoints served by the Gigawatt backend:
// `<conversations>/chat/info` (runtime discovery) and `<conversations>/chat/agent/:agentId/{run,connect}` (SSE streams).
const chatRuntimeUrl =
  '*/organizations/:organizationId/users/me/conversations/chat';

const encoder = new TextEncoder();

const sseResponse = (events: object[]) =>
  new HttpResponse(
    new ReadableStream({
      async start(controller) {
        for (const event of events) {
          controller.enqueue(
            encoder.encode(`data: ${JSON.stringify(event)}\n\n`)
          );
          await delay(50);
        }
        controller.close();
      },
    }),
    { headers: { 'Content-Type': 'text/event-stream' } }
  );

// Gigawatt backend (AI chat) responses are snake_case and not typed by an API
// client yet, so they use the DTOs defined in gigawattBackendApiSlice.ts.
export const conversationHandlers = [
  http.post(
    '*/organizations/:organizationId/users/me/conversations',
    async ({ params }) => {
      const now = new Date();
      const expiresAt = new Date(now.getTime() + 24 * 60 * 60 * 1000);

      const conversation: AiConversationDto = {
        conversation_id: `conversation-${crypto.randomUUID()}`,
        user_id: simpleUser.id,
        organization_id: String(params.organizationId),
        created_at: now.toISOString(),
        expires_at: expiresAt.toISOString(),
      };

      await delay();
      return HttpResponse.json(conversation, { status: 201 });
    }
  ),

  // A fresh conversation has no persisted messages yet.
  http.get(
    '*/organizations/:organizationId/users/me/conversations/:conversationId',
    async () => {
      await delay();
      return HttpResponse.json([], { status: 200 });
    }
  ),

  http.put(
    '*/organizations/:organizationId/users/me/conversations/:conversationId/messages/:messageId/feedback',
    async ({ params, request }) => {
      const body = (await request.json()) as {
        rating: 'up' | 'down' | null;
        reasons: FeedbackReason[];
      };
      const feedback: UpdateMessageFeedbackResponseDto = {
        message_id: String(params.messageId),
        rating: body.rating,
        reasons: body.reasons,
        feedback_updated_at: new Date().toISOString(),
      };

      await delay();
      return HttpResponse.json(feedback, { status: 200 });
    }
  ),

  http.get(`${chatRuntimeUrl}/info`, () =>
    HttpResponse.json({
      version: 'mock',
      mode: 'sse',
      audioFileTranscriptionEnabled: false,
      agents: { default: { name: 'default', description: 'Mocked agent' } },
    })
  ),

  // Restoring a thread: nothing to replay
  http.post(`${chatRuntimeUrl}/agent/:agentId/connect`, async ({ request }) => {
    const { threadId, runId } = (await request.json()) as {
      threadId: string;
      runId: string;
    };
    return sseResponse([
      { type: 'RUN_STARTED', threadId, runId },
      { type: 'RUN_FINISHED', threadId, runId },
    ]);
  }),

  // Every user message gets the same canned answer
  http.post(`${chatRuntimeUrl}/agent/:agentId/run`, async ({ request }) => {
    const { threadId, runId } = (await request.json()) as {
      threadId: string;
      runId: string;
    };
    const messageId = `message-${crypto.randomUUID()}`;
    return sseResponse([
      { type: 'RUN_STARTED', threadId, runId },
      { type: 'TEXT_MESSAGE_START', messageId, role: 'assistant' },
      {
        type: 'TEXT_MESSAGE_CONTENT',
        messageId,
        delta: 'This is a mocked response from the IT Hub assistant.',
      },
      { type: 'TEXT_MESSAGE_END', messageId },
      { type: 'RUN_FINISHED', threadId, runId },
    ]);
  }),
];
